import React from "react";
import AppCard from "../../UI/AppCards";
import { addDays } from "date-fns";
import { NewLocationDetails } from "../../../@types/DTO/Locations";
import DateTimePicker from "./DateTimePicker";
import { AppInputSwitcher } from "../AppInputSwitcher/AppInputSwitcher";
import AppButton from "../../UI/AppButtons";

interface BookingFormContentProps {
  pickupLocation: string;
  // reservationDestination?: string;
  reservationStartDate: Date | null;
  reservationEndDate: Date | null;
  locationDetail: NewLocationDetails[];
  minRentalAllowedDay: number;
  maxRentalAllowedDay: number;
  isSmallScreen: boolean;
  istabletScreen: boolean;
  translate: (key: string) => string;
  onPickupLocationChange: (value: string) => void;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  isDroppingAtSameLocation: string;
}

const getDisabledDays = (
  pickupLocation: string,
  locationDetail: NewLocationDetails[]
): number[] => {
  const location = locationDetail.find(
    (e) => e.locationName?.toLowerCase() === pickupLocation.toLowerCase()
  );
  if (!location) return [];

  const allDays = {
    0: true,
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
  };
  const openDays = location.openings?.map((e) => e.dayOfTheWeek % 7) || [];

  openDays.forEach((day) => delete allDays[day]);
  return Object.keys(allDays).map((d) => parseInt(d));
};

const LocationInput = ({
  pickupLocation,
  locationDetail,
  translate,
  onLocationChange,
  isSmallScreen,
}: {
  pickupLocation: string;
  locationDetail: NewLocationDetails[];
  translate: (key: string) => string;
  onLocationChange: (value: string) => void;
  isSmallScreen: boolean;
}) => (
  <AppInputSwitcher
    className={"w-full"}
    containerheight={"63px"}
    fontFamilyForLabel={"MBCorpoAScreen1Cond-Regular-Web"}
    fontFamilyForInput={"MBCorpoSText-CondRegular-Web"}
    containerwidth={isSmallScreen ? "" : "320px"}
    value={pickupLocation}
    label={translate("pick up location")}
    style={{
      fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
      width: "320px",
    }}
    key={"pickUpLocation"}
    showlabel={true}
    showlabelOnShrink={true}
    options={locationDetail?.map((e) => ({
      label: e.locationName,
      value: e.locationName,
    }))}
    type="select"
    variant="with-search"
    isMargin={true}
    onchange={onLocationChange}
    disableInput={true}
  />
);

const SubmitButton = ({
  translate,
  isSmallScreen,
}: {
  translate: (key: string) => string;
  isSmallScreen: boolean;
}) => (
  <AppButton
    type={"submit"}
    className={`h-[63px] md:float-right md:ml-auto w-full`}
    style={{
      marginTop: "31px",
      background: "#0078D6",
      width: isSmallScreen ? "100%" : "320px",
    }}
  >
    <p
      className={`w-full text-center`}
      style={{
        fontFamily: "MBCorpoSText-Light-Web-Text-Light",
        textTransform: "none",
      }}
    >
      {translate("Search Vehicle")}
    </p>
  </AppButton>
);

export const BookingFormContent = ({
  pickupLocation,
  // reservationDestination,
  reservationStartDate,
  reservationEndDate,
  locationDetail,
  minRentalAllowedDay,
  maxRentalAllowedDay,
  isSmallScreen,
  istabletScreen,
  translate,
  onPickupLocationChange,
  onStartDateChange,
  onEndDateChange,
}: BookingFormContentProps) => {
  const pickupMinDate = addDays(new Date(), minRentalAllowedDay);
  const dropoffMinDate = addDays(
    reservationStartDate || new Date(),
    minRentalAllowedDay
  );
  const dropoffMaxDate = addDays(
    reservationStartDate || new Date(),
    maxRentalAllowedDay
  );

  return (
    <AppCard className="flex flex-col w-full box-border justify-center items-center p-4 gap-4 py-8">
      <div className="flex xl:flex-row gap-2 flex-col w-full">
        <div
          className={`md:grow flex ${
            istabletScreen
              ? "flex-col"
              : "md:flex-row gap-2 flex-col min-w-[0rem] md:min-w-[25rem] ml-[8px] md:ml-0"
          }`}
          style={{
            marginBottom: "24px",
            marginLeft: !isSmallScreen ? "13px" : "",
          }}
        >
          <div style={{ marginRight: "13px" }}>
            <LocationInput
              pickupLocation={pickupLocation}
              locationDetail={locationDetail}
              translate={translate}
              onLocationChange={onPickupLocationChange}
              isSmallScreen={isSmallScreen}
            />
          </div>

          <div style={{ marginRight: "13px", marginBottom: "40px" }}>
            <DateTimePicker
              placeholder={translate("Select Pick-up Date and Time")}
              label={translate("Pick-up Date and Time")}
              isSmallScreen={isSmallScreen}
              disableOnDay={
                pickupLocation && locationDetail.length
                  ? getDisabledDays(pickupLocation, locationDetail)
                  : []
              }
              minDate={pickupMinDate}
              maxDate={undefined}
              locationDetail={locationDetail}
              locationName={pickupLocation}
              onchange={onStartDateChange}
              startDate={reservationStartDate}
              endDate={reservationEndDate}
              datePickerFor={"pickupTime"}
            />
          </div>

          <div style={{ marginRight: "13px", marginBottom: "40px" }}>
            <DateTimePicker
              placeholder={translate("Select Drop-off Date and Time")}
              label={translate("Drop-off Date and Time")}
              isSmallScreen={isSmallScreen}
              disableOnDay={
                pickupLocation && locationDetail.length
                  ? getDisabledDays(pickupLocation, locationDetail)
                  : []
              }
              minDate={dropoffMinDate}
              maxDate={dropoffMaxDate}
              locationDetail={locationDetail}
              locationName={pickupLocation}
              onchange={onEndDateChange}
              startDate={reservationStartDate}
              endDate={reservationEndDate}
              datePickerFor={"dropOffTime"}
            />
          </div>

          <div style={{ marginRight: "13px" }}>
            <SubmitButton translate={translate} isSmallScreen={isSmallScreen} />
          </div>
        </div>
      </div>
    </AppCard>
  );
};
